import React, { useRef, useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import ApiService from "../../services/Api.service";

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const AudioPlayer = ({ url, callId, onTimeUpdate }) => {
  const audioRef = useRef(null);
  const [activeUrl, setActiveUrl] = useState(url);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [showVolume, setShowVolume] = useState(false);
  const [waveformBars] = useState(() =>
    Array.from({ length: 50 }, () => Math.random() * 0.7 + 0.3)
  );

  useEffect(() => {
    if (url) {
      setActiveUrl(url);
    }
  }, [url]);

  useEffect(() => {
    if (callId && !url) {
      ApiService.getRecordingSignedUrl(callId)
        .then((res) => {
          if (res?.data?.data) {
            setActiveUrl(res.data.data);
          }
        })
        .catch((err) => console.warn("Failed to fetch recording url:", err));
    }
  }, [callId, url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && activeUrl) {
      audio.load();
      setCurrentTime(0);
      setIsPlaying(false);
    }
  }, [activeUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (onTimeUpdate) onTimeUpdate(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [onTimeUpdate]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!activeUrl && callId) {
      try {
        const res = await ApiService.getRecordingSignedUrl(callId);
        if (res?.data?.data) {
          setActiveUrl(res.data.data);
          setTimeout(() => {
            audioRef.current?.play().then(() => setIsPlaying(true)).catch((e) => console.error("Play error:", e));
          }, 150);
          return;
        }
      } catch (err) {
        console.error("Failed to load recording:", err);
      }
    }

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch((e) => {
        console.error("Audio playback error:", e);
        setIsPlaying(false);
      });
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (isMuted) {
      audio.volume = volume || 1;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const cycleSpeed = () => {
    const currentIndex = SPEED_OPTIONS.indexOf(speed);
    const nextIndex = (currentIndex + 1) % SPEED_OPTIONS.length;
    const newSpeed = SPEED_OPTIONS[nextIndex];
    setSpeed(newSpeed);
    audioRef.current.playbackRate = newSpeed;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={styles.wrapper}>
      <audio ref={audioRef} src={activeUrl || url} preload="metadata" />

      <div style={styles.container}>
        {/* Play/Pause Button */}
        <button onClick={togglePlay} style={styles.playBtn}>
          {isPlaying ? <Pause size={16} color="#fff" /> : <Play size={16} color="#fff" />}
        </button>

        {/* Waveform + Progress */}
        <div style={styles.waveformContainer} onClick={handleSeek}>
          <div style={styles.waveform}>
            {waveformBars.map((height, i) => {
              const barProgress = (i / waveformBars.length) * 100;
              const isActive = barProgress <= progress;
              return (
                <div
                  key={i}
                  style={{
                    ...styles.bar,
                    height: `${height * 100}%`,
                    backgroundColor: isActive ? "#5b5fc7" : "#c4c4c4",
                  }}
                />
              );
            })}
          </div>

          {/* Progress line */}
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
            <div style={{ ...styles.progressDot, left: `${progress}%` }} />
          </div>
        </div>

        {/* Time */}
        <div style={styles.timeDisplay}>
          <span style={styles.time}>{formatTime(currentTime)}</span>
          <span style={styles.timeSeparator}>/</span>
          <span style={styles.time}>{formatTime(duration)}</span>
        </div>

        {/* Speed Control */}
        <button onClick={cycleSpeed} style={styles.speedBtn} title="Playback speed">
          {speed}x
        </button>

        {/* Volume Control */}
        <div
          style={styles.volumeWrapper}
          onMouseEnter={() => setShowVolume(true)}
          onMouseLeave={() => setShowVolume(false)}
        >
          <button onClick={toggleMute} style={styles.volumeBtn} title="Volume">
            {isMuted ? <VolumeX size={16} color="#555" /> : <Volume2 size={16} color="#555" />}
          </button>

          {showVolume && (
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              style={styles.volumeSlider}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    width: "100%",
  },
  container: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    backgroundColor: "#f5f5f7",
    borderRadius: "50px",
    width: "100%",
    boxSizing: "border-box",
  },
  playBtn: {
    width: "36px",
    height: "36px",
    minWidth: "36px",
    borderRadius: "50%",
    backgroundColor: "#2b3a67",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s",
  },
  waveformContainer: {
    flex: 1,
    cursor: "pointer",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  waveform: {
    display: "flex",
    alignItems: "center",
    gap: "1.5px",
    height: "28px",
  },
  bar: {
    flex: 1,
    borderRadius: "2px",
    transition: "background-color 0.1s",
    minWidth: "2px",
  },
  progressTrack: {
    position: "relative",
    height: "3px",
    backgroundColor: "#ddd",
    borderRadius: "3px",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#5b5fc7",
    borderRadius: "3px",
    transition: "width 0.1s linear",
  },
  progressDot: {
    position: "absolute",
    top: "-3px",
    width: "10px",
    height: "10px",
    backgroundColor: "#5b5fc7",
    borderRadius: "50%",
    transform: "translateX(-50%)",
    transition: "left 0.1s linear",
  },
  timeDisplay: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    minWidth: "70px",
    justifyContent: "center",
  },
  time: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#555",
  },
  timeSeparator: {
    fontSize: "11px",
    color: "#999",
  },
  speedBtn: {
    padding: "4px 8px",
    borderRadius: "12px",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: 600,
    color: "#2b3a67",
    minWidth: "36px",
    textAlign: "center",
    transition: "background 0.2s",
  },
  volumeWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  volumeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    padding: "4px",
    borderRadius: "4px",
  },
  volumeSlider: {
    width: "60px",
    height: "4px",
    cursor: "pointer",
    accentColor: "#5b5fc7",
  },
};

export default AudioPlayer;
